import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SectionHeader } from "../components/UI";
import {
  BookOpen, Compass, ShieldCheck, Sparkles, LifeBuoy, ScrollText,
  Workflow as WorkflowIcon, FileText, Users, Lock, Search, ChevronRight,
  ExternalLink, Mail, MessageSquare, PlayCircle, KeyRound, Building2,
  BadgeCheck, CreditCard, ClipboardCheck, HelpCircle, AlertTriangle,
  Fingerprint, Rocket, Send, ClipboardList,
} from "lucide-react";

/* ------------------------------------------------------------------
 * Help & Docs , enterprise help center for PropHR
 * -----------------------------------------------------------------*/

const ROLE_LABEL = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  hr: "HR Business Partner",
  manager: "People Manager",
  employee: "Employee",
};

/* Getting-started guides tailored per role */
const ROLE_GUIDES = {
  employee: {
    color: "from-blue-50 to-white",
    title: "Getting started , Employees",
    subtitle: "How to report a case, sign policies, and track outcomes with full confidentiality.",
    steps: [
      { icon: FileText, title: "Report a case", body: "Use the 6-step wizard to describe the issue. You can attach files, record voice notes, and submit anonymously with a legal signature." },
      { icon: ShieldCheck, title: "Understand your privacy", body: "Anonymous cases mask your identity from managers and other employees. HR, Company Admin, and PropHR can still contact you securely." },
      { icon: ClipboardList, title: "Track status & communicate", body: "Open your case to see the workflow timeline, sign policies, and message HR through the encrypted communication stream." },
      { icon: BookOpen, title: "Sign required policies", body: "Any policy pending your signature appears in the Policies tab. Read carefully, type your legal name, and acknowledge to complete." },
    ],
  },
  manager: {
    color: "from-indigo-50 to-white",
    title: "Getting started , People Managers",
    subtitle: "How to review team cases, upload evidence, and complete workflow steps assigned to you.",
    steps: [
      { icon: FileText, title: "Review team cases", body: "Cases involving your direct reports appear in Team Cases. Reporter identity is masked when the case is anonymous." },
      { icon: ClipboardList, title: "Upload evidence", body: "Attach documents, chat logs, or files to a case. Evidence is fingerprinted (SHA-256) and cannot be deleted, only archived by Company Admin." },
      { icon: WorkflowIcon, title: "Complete assigned steps", body: "When a workflow step is assigned to you, complete it from the Workflow tab with a note. The engine auto-advances to the next step." },
      { icon: ShieldCheck, title: "Retaliation protection", body: "Never take action against a reporter. Retaliation is a policy violation and is logged permanently in the audit trail." },
    ],
  },
  hr: {
    color: "from-emerald-50 to-white",
    title: "Getting started , HR Business Partners",
    subtitle: "How to triage cases, submit findingsList, and use Sentinel Analytics for balanced decisions.",
    steps: [
      { icon: FileText, title: "Triage inbound cases", body: "New cases arrive in the case queue with a suggested workflow, priority, and Sentinel Analytics summary." },
      { icon: Sparkles, title: "Use Sentinel Analytics", body: "Open a case to see Dual-Perspective analysis, key risks, and suggested next actions. AI outputs are advisory , you make the final call." },
      { icon: ClipboardCheck, title: "Submit findings", body: "Draft findings with rationale. It's routed to Company Admin for approval before they become binding." },
      { icon: ScrollText, title: "Publish & sign policies", body: "Create policies in the WYSIWYG editor. On approval by Company Admin, employees are prompted to sign with typed legal name." },
    ],
  },
  company_admin: {
    color: "from-amber-50 to-white",
    title: "Getting started , Company Admins",
    subtitle: "How to configure your workspace, approve findingsList, and manage users, policies, and workflows.",
    steps: [
      { icon: Building2, title: "Complete Company Profile", body: "Fill the 6-step onboarding: basics, location, contacts, org structure, branding, review. This raises your workspace completion score." },
      { icon: Users, title: "Manage users & roles", body: "Invite HR, Managers, and Employees. Assign roles carefully , role changes are logged in the audit log." },
      { icon: WorkflowIcon, title: "Design workflows", body: "Clone a system template or start fresh. Steps carry assignee roles and SLA hours. The runtime engine auto-routes new cases through these steps." },
      { icon: ClipboardCheck, title: "Approve findings & policies", body: "Findings and policies flow to Approvals. Approve or reject with a note , every action is fingerprinted." },
      { icon: CreditCard, title: "Manage billing", body: "Switch plans, download invoices, and monitor seat usage. Contact PropHR support for enterprise arrangements." },
    ],
  },
  super_admin: {
    color: "from-slate-100 to-white",
    title: "Getting started , Super Admin (PropHR)",
    subtitle: "How to onboard tenants, respond to support tickets, and run cross-portfolio Enterprise Intelligence.",
    steps: [
      { icon: Building2, title: "Onboard a tenant", body: "Create tenants from the Tenants tab. Each tenant gets an isolated data namespace and a seeded Company Admin." },
      { icon: LifeBuoy, title: "Handle support tickets", body: "Support tickets from all tenants flow into the Support Desk. Respond with the threaded reply UI and update status." },
      { icon: Sparkles, title: "Enterprise Intelligence", body: "Cross-portfolio insights aggregate risk signals across tenants without exposing tenant-identifying details." },
      { icon: WorkflowIcon, title: "Publish workflow templates", body: "Ship reusable workflow blueprints. Any tenant can clone and customize them for their own operations." },
      { icon: ClipboardList, title: "Audit everything", body: "Every mutating action is logged in the audit trail. Filter by tenant, actor, or resource type." },
    ],
  },
};

/* Docs library , always visible to all roles */
const DOC_SECTIONS = [
  {
    key: "cases",
    icon: FileText,
    title: "Cases & Reporting",
    color: "text-blue-700",
    docs: [
      { title: "How to file a case", body: "Open Report a Case, pick a category, describe the incident, attach evidence, and choose anonymous or identified. You can withdraw a case any time before findings are issued." },
      { title: "Anonymous reporting , what's masked", body: "Your name, email, avatar, and manager linkage are hidden from other employees and your direct manager. HR, Company Admin, and Super Admin can still reach out through the encrypted stream." },
      { title: "Voice notes & auto-transcription", body: "Record up to 5 minutes. Whisper transcribes automatically so you never need to type long descriptions. Both audio and transcript are stored with SHA-256 fingerprints." },
      { title: "Withdrawing a case", body: "From the case detail page, click Withdraw. A confirmation modal will ask for a reason. Withdrawn cases stay in the audit trail but are marked closed." },
    ],
  },
  {
    key: "policies",
    icon: ScrollText,
    title: "Policies & Signatures",
    color: "text-emerald-700",
    docs: [
      { title: "Signing a policy", body: "Open the policy, read it in full, type your legal name exactly as it appears on your profile, tick the agreement box, and submit. Your signature is stored with a SHA-256 fingerprint." },
      { title: "Where to find your signed policies", body: "Go to Profile → My Signed Policies. Every acknowledgment is listed with the date, policy version, and downloadable PDF." },
      { title: "Policy lifecycle", body: "Draft → Pending approval → Approved → Signed by employees. Only Company Admin can approve. Policies can be archived but never deleted." },
    ],
  },
  {
    key: "workflows",
    icon: WorkflowIcon,
    title: "Workflows",
    color: "text-indigo-700",
    docs: [
      { title: "How the runtime engine works", body: "When a case is created, the engine picks the most specific active workflow whose trigger matches your case category. Each step has an assignee role, SLA hours, and required actions." },
      { title: "Advancing a step", body: "The assigned user opens the Workflow tab and either marks the step complete or skips it with a note. Domain actions (evidence added, findings submitted, findings approved) also auto-advance matching steps." },
      { title: "SLA & due dates", body: "Every step carries cumulative SLA. You'll see the due date in the timeline. Overdue steps are highlighted in amber." },
      { title: "Designing custom workflows", body: "Company Admins can start from scratch or clone a system template. Reorder steps, set assignee roles, and adjust SLA in the visual editor." },
    ],
  },
  {
    key: "ai",
    icon: Sparkles,
    title: "Sentinel Analytics (AI)",
    color: "text-violet-700",
    docs: [
      { title: "What Sentinel Analytics does", body: "Sentinel Analytics produces case summaries, dual-perspective analysis, and risk indicators. All outputs are advisory and clearly labeled. Humans always own the decision." },
      { title: "Dual Perspective", body: "The AI models both the reporter's and the subject's perspective side-by-side to surface bias and reduce blind spots." },
      { title: "Enterprise Intelligence", body: "Cross-case pattern analysis. For Company Admins, this stays within your tenant. For Super Admins, it aggregates across the portfolio without leaking tenant-identifying data." },
      { title: "What Sentinel does NOT do", body: "It never issues findingsList, never contacts employees on your behalf, and never trains external models on your data." },
    ],
  },
  {
    key: "privacy",
    icon: ShieldCheck,
    title: "Privacy, Retention & Compliance",
    color: "text-teal-700",
    docs: [
      { title: "Compliance frameworks", body: "PropHR is built for GDPR, CCPA, EEOC, SOC 2 Type II, and ISO 27001. Every mutating action is written to the immutable audit log." },
      { title: "Data retention", body: "Each tenant has a retention policy (default 2555 days ≈ 7 years). Old cases are automatically purged unless placed on legal hold." },
      { title: "Anonymous & legal signatures", body: "Anonymous cases require a typed legal-name signature and are bound to a hashed IP + user-agent client fingerprint to protect against abuse while preserving anonymity from the workspace." },
      { title: "Retaliation protection", body: "Any action that could be construed as retaliation triggers an audit flag. PropHR maintains this as a policy default and cannot be turned off." },
    ],
  },
  {
    key: "security",
    icon: Lock,
    title: "Security & Access",
    color: "text-rose-700",
    docs: [
      { title: "Authentication", body: "PropHR supports JWT-based email login. Google Workspace and Microsoft 365 SSO are on the roadmap , buttons exist but the flow is pending IT hookup." },
      { title: "Role-based access control", body: "Every endpoint enforces role gates on the server. Sensitive fields (reporter identity, legal signatures) are stripped at read-time for non-privileged viewers." },
      { title: "Fingerprinting", body: "Evidence, findingsList, policies, and legal signatures are hashed with SHA-256. The fingerprint chip on every artifact lets you copy the hash and verify integrity." },
      { title: "Changing your password", body: "Profile → Security → Change password. All password changes trigger an audit event and, once email is configured, a notification to your registered address." },
    ],
  },
];

/* Frequently asked questions */
const FAQS = [
  { q: "Can my manager see who filed an anonymous case?", a: "No. Anonymous cases hide the reporter's identity from managers and other employees. HR, Company Admin, and Super Admin can still contact you securely through the case's encrypted communication stream." },
  { q: "How do I know a policy is authentic?", a: "Every policy has a SHA-256 fingerprint. When you sign, your signature is bound to that fingerprint. If the policy is ever modified, the fingerprint changes and old signatures are invalidated automatically." },
  { q: "What happens if the AI gets it wrong?", a: "Sentinel Analytics outputs are advisory. You are never required to accept them. Every case detail page shows a warning card reminding you that the final decision belongs to humans." },
  { q: "Can I delete an uploaded file?", a: "No. Evidence cannot be deleted , only archived by a Company Admin. This preserves chain-of-custody and is required for compliance." },
  { q: "How long is my data kept?", a: "Each tenant sets a retention window (default ≈ 7 years). Cases past the window are purged automatically unless placed on legal hold. Ask your Company Admin for your workspace's exact setting." },
  { q: "Who can approve findings?", a: "Only the Company Admin can approve or reject findings. HR drafts and submits, Admin reviews. This two-person integrity control is a compliance requirement." },
  { q: "Does PropHR train AI models on my data?", a: "No. Your tenant data is never used for external model training. AI processing runs against secure, PropHR-managed inference endpoints with strict contractual data-use restrictions." },
  { q: "How do I contact PropHR support?", a: "From the Support Desk (LifeBuoy icon in the sidebar) file a ticket, or email support@prophr.com. Enterprise customers have a dedicated Slack channel , ask your account manager." },
];

/* Escalation matrix */
const ESCALATIONS = [
  { severity: "Question / How-to", target: "Help Center + Support Ticket", sla: "1 business day", color: "border-blue-300 bg-blue-50 text-blue-900" },
  { severity: "Product bug", target: "Support Desk (Category: Technical Issue)", sla: "1 business day", color: "border-slate-300 bg-slate-50 text-slate-900" },
  { severity: "Billing / access", target: "Support Desk (Category: Billing or Account)", sla: "same business day", color: "border-amber-300 bg-amber-50 text-amber-900" },
  { severity: "Security / data incident", target: "security@prophr.com + Support ticket flagged Critical", sla: "1 hour", color: "border-rose-300 bg-rose-50 text-rose-900" },
  { severity: "Legal / compliance", target: "legal@prophr.com + your Company Admin", sla: "1 business day", color: "border-violet-300 bg-violet-50 text-violet-900" },
];

/* Keyboard shortcuts */
const SHORTCUTS = [
  { keys: ["⌘", "K"], desc: "Open global search" },
  { keys: ["G", "D"], desc: "Go to Dashboard" },
  { keys: ["G", "C"], desc: "Go to Cases" },
  { keys: ["N", "C"], desc: "Report a new case" },
  { keys: ["?"], desc: "Show shortcuts overlay" },
  { keys: ["Esc"], desc: "Close modals & dropdowns" },
];

export default function HelpDocs() {
  const { user } = useAuth();
  const roleGuide = ROLE_GUIDES[user?.role] || ROLE_GUIDES.employee;
  const [q, setQ] = useState("");
  const [activeSection, setActiveSection] = useState("cases");
  const [openFaq, setOpenFaq] = useState(null);

  const filteredSections = useMemo(() => {
    if (!q.trim()) return DOC_SECTIONS;
    const needle = q.toLowerCase();
    return DOC_SECTIONS
      .map((s) => ({
        ...s,
        docs: s.docs.filter(
          (d) => d.title.toLowerCase().includes(needle) || d.body.toLowerCase().includes(needle),
        ),
      }))
      .filter((s) => s.docs.length > 0 || s.title.toLowerCase().includes(needle));
  }, [q]);

  const filteredFaqs = useMemo(() => {
    if (!q.trim()) return FAQS;
    const needle = q.toLowerCase();
    return FAQS.filter((f) => f.q.toLowerCase().includes(needle) || f.a.toLowerCase().includes(needle));
  }, [q]);

  const current = filteredSections.find((s) => s.key === activeSection) || filteredSections[0];

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="help-docs-page">
      {/* Hero */}
      <div className={`relative overflow-hidden border border-slate-200 bg-gradient-to-br ${roleGuide.color} p-8 mb-8`}>
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-blue-100/40 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-3">
            <HelpCircle size={12} strokeWidth={2} />
            <span>Help Center · Signed in as {ROLE_LABEL[user?.role] || "Guest"}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-tight max-w-3xl">
            Everything you need to run PropHR with confidence.
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl">
            Product guides, compliance playbooks, and support paths , curated for your role. Search across docs, FAQs, and shortcuts.
          </p>

          {/* Search */}
          <div className="mt-6 relative max-w-2xl">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search docs, guides & FAQs…"
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 shadow-sm"
              data-testid="help-search"
            />
          </div>

          {/* Quick shortcuts */}
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { to: "/cases/new", icon: FileText, label: "Report a case" },
              { to: "/tickets", icon: LifeBuoy, label: "Contact support" },
              { to: "/policies", icon: ScrollText, label: "Policies" },
              { to: "/profile", icon: KeyRound, label: "Account & security" },
            ].map((s) => (
              <Link
                key={s.label}
                to={s.to}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-400 text-xs text-slate-700 rounded transition-colors"
                data-testid={`help-quick-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <s.icon size={12} strokeWidth={1.75} /> {s.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Role-specific getting started */}
      <section className="mb-12" data-testid="help-role-guide">
        <div className="flex items-center gap-2 mb-4">
          <Rocket size={16} className="text-blue-700" strokeWidth={2} />
          <div className="eyebrow text-blue-700">Getting started for your role</div>
        </div>
        <SectionHeader title={roleGuide.title} subtitle={roleGuide.subtitle} />
        <div className="grid md:grid-cols-2 gap-4">
          {roleGuide.steps.map((s, i) => (
            <div
              key={s.title}
              className="border border-slate-200 bg-white p-5 hover:border-slate-400 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <s.icon size={16} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-slate-400">Step {i + 1}</span>
                  </div>
                  <div className="font-display text-base font-semibold text-slate-900">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1 leading-relaxed">{s.body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Docs library */}
      <section className="mb-12" data-testid="help-docs-library">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={16} className="text-emerald-700" strokeWidth={2} />
          <div className="eyebrow text-emerald-700">Documentation library</div>
        </div>
        <SectionHeader
          title="Browse by topic"
          subtitle="Deep-dive documentation on every major surface of PropHR."
        />
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          {/* Sidebar , sections */}
          <aside className="space-y-1">
            {filteredSections.map((s) => {
              const active = activeSection === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={
                    "w-full text-left px-3 py-2 flex items-center gap-2 text-sm border-l-2 transition-colors " +
                    (active
                      ? "border-slate-900 bg-slate-50 text-slate-900 font-medium"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50")
                  }
                  data-testid={`help-section-${s.key}`}
                >
                  <s.icon size={14} strokeWidth={1.75} className={active ? s.color : "text-slate-400"} />
                  <span className="flex-1">{s.title}</span>
                  <span className="text-[10px] font-mono text-slate-400">{s.docs.length}</span>
                </button>
              );
            })}
            {filteredSections.length === 0 && (
              <div className="text-xs text-slate-500 italic px-3 py-2">No matches</div>
            )}
          </aside>

          {/* Content */}
          <div>
            {current ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                  <current.icon size={18} className={current.color} strokeWidth={1.75} />
                  <div className="font-display text-xl font-semibold text-slate-900">{current.title}</div>
                </div>
                {current.docs.map((d, i) => (
                  <details
                    key={d.title}
                    open={i === 0}
                    className="border border-slate-200 bg-white group"
                    data-testid={`help-doc-${current.key}-${i}`}
                  >
                    <summary className="cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <span className="text-sm font-semibold text-slate-900">{d.title}</span>
                      <ChevronRight size={14} className="text-slate-400 group-open:rotate-90 transition-transform" strokeWidth={1.75} />
                    </summary>
                    <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                      {d.body}
                    </div>
                  </details>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 bg-slate-50/50">
                No documentation matches your search.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="mb-12" data-testid="help-faqs">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={16} className="text-violet-700" strokeWidth={2} />
          <div className="eyebrow text-violet-700">Frequently asked</div>
        </div>
        <SectionHeader
          title="Common questions"
          subtitle="Answers to the questions we hear most from workspace admins, HR, managers, and employees."
        />
        <div className="grid md:grid-cols-2 gap-3">
          {filteredFaqs.map((f, i) => (
            <button
              key={f.q}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              className="text-left border border-slate-200 bg-white p-4 hover:border-slate-400 transition-colors"
              data-testid={`help-faq-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-sm text-slate-900">{f.q}</div>
                <ChevronRight
                  size={14}
                  className={"text-slate-400 shrink-0 mt-0.5 transition-transform " + (openFaq === i ? "rotate-90" : "")}
                  strokeWidth={1.75}
                />
              </div>
              {openFaq === i && (
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</div>
              )}
            </button>
          ))}
          {filteredFaqs.length === 0 && (
            <div className="col-span-2 border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 bg-slate-50/50">
              No FAQs match your search. Try a different keyword or contact support.
            </div>
          )}
        </div>
      </section>

      {/* Escalation matrix */}
      <section className="mb-12" data-testid="help-escalations">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={16} className="text-amber-700" strokeWidth={2} />
          <div className="eyebrow text-amber-700">Escalation paths</div>
        </div>
        <SectionHeader
          title="Where to send what"
          subtitle="Route issues to the right team the first time. Every ticket you file feeds directly into the PropHR support desk."
        />
        <div className="border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-widest text-slate-400 bg-slate-50">
                <th className="text-left px-4 py-2.5 font-mono font-normal">Severity</th>
                <th className="text-left px-4 py-2.5 font-mono font-normal">Route to</th>
                <th className="text-left px-4 py-2.5 font-mono font-normal">Target response</th>
              </tr>
            </thead>
            <tbody>
              {ESCALATIONS.map((e) => (
                <tr key={e.severity} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-3">
                    <span className={"inline-block px-2 py-0.5 text-xs border font-medium " + e.color}>{e.severity}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-800">{e.target}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{e.sla}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Shortcuts */}
      <section className="mb-12" data-testid="help-shortcuts">
        <div className="flex items-center gap-2 mb-4">
          <PlayCircle size={16} className="text-slate-700" strokeWidth={2} />
          <div className="eyebrow text-slate-700">Power user</div>
        </div>
        <SectionHeader title="Keyboard shortcuts" subtitle="Move through PropHR without lifting your hands from the keyboard." />
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {SHORTCUTS.map((s) => (
            <div key={s.desc} className="border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-700">{s.desc}</div>
              <div className="flex items-center gap-1 shrink-0">
                {s.keys.map((k) => (
                  <kbd key={k} className="px-1.5 py-0.5 border border-slate-300 bg-slate-50 rounded text-[10px] font-mono text-slate-700">
                    {k}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Compliance callouts */}
      <section className="mb-12" data-testid="help-compliance">
        <div className="flex items-center gap-2 mb-4">
          <Fingerprint size={16} className="text-teal-700" strokeWidth={2} />
          <div className="eyebrow text-teal-700">Compliance at a glance</div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, title: "GDPR / CCPA", body: "Full data-subject rights: access, rectification, erasure, portability. Contact your Company Admin to raise a request." },
            { icon: BadgeCheck, title: "SOC 2 Type II · ISO 27001", body: "Continuous auditing, encryption at rest and in transit, and independent third-party audits every 12 months." },
            { icon: ScrollText, title: "EEOC recordkeeping", body: "Case records retained per federal recordkeeping requirements. Legal hold overrides retention purges when in effect." },
          ].map((c) => (
            <div key={c.title} className="border border-slate-200 bg-white p-5">
              <c.icon size={20} className="text-teal-700 mb-3" strokeWidth={1.75} />
              <div className="font-display text-base font-semibold text-slate-900">{c.title}</div>
              <div className="text-sm text-slate-600 mt-1 leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact block */}
      <section
        className="border border-slate-800 bg-slate-900 text-white p-8 relative overflow-hidden"
        data-testid="help-contact-block"
      >
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
        <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-slate-400 uppercase mb-2">
              <Compass size={12} strokeWidth={2} />
              <span>Still need help?</span>
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight leading-tight">
              Talk to a human at PropHR.
            </h2>
            <p className="text-slate-300 mt-2 max-w-2xl">
              File a ticket to reach the support desk directly, or email us for anything urgent. Enterprise customers can request a dedicated Slack channel , ask your account manager.
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Link
              to="/tickets"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold transition-colors"
              data-testid="help-open-tickets"
            >
              <Send size={14} strokeWidth={2} /> Open a support ticket
            </Link>
            <a
              href="mailto:support@prophr.com"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 border border-slate-700 hover:border-slate-500 text-sm text-slate-100 transition-colors"
              data-testid="help-email-support"
            >
              <Mail size={14} strokeWidth={1.75} /> support@prophr.com
            </a>
            <a
              href="https://prophr.com/status"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2 text-xs text-slate-400 hover:text-slate-100 transition-colors"
            >
              <ExternalLink size={11} strokeWidth={2} /> System status
            </a>
          </div>
        </div>
      </section>

      {/* Version footer */}
      <div className="mt-8 text-center text-[10px] font-mono text-slate-400 uppercase tracking-widest">
        PropHR · Help Center · Rev 2026.02
      </div>
    </div>
  );
}
